import { useId, useMemo, useRef, useState } from 'react';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import Select from '@/components/ui/select.jsx';
import { useChoferMutations } from '@/hooks/useChoferes.js';

const INITIAL_STATE = {
  apellido: '',
  nombre: '',
  nroDocumento: '',
  telefonoMovil: '',
  licenciaClase: 'C',
  licenciaVencimiento: '',
};

function resolveStoredContext() {
  if (typeof window === 'undefined') {
    return { empresaId: null, sucursalId: null };
  }

  try {
    const tenantRaw = window.localStorage.getItem('tenant');
    const userRaw = window.localStorage.getItem('user');
    const tenant = tenantRaw ? JSON.parse(tenantRaw) : null;
    const user = userRaw ? JSON.parse(userRaw) : null;

    return {
      empresaId:
        tenant?.id
        || tenant?.empresa_id
        || tenant?.empresaId
        || tenant?.empresa?.id
        || user?.empresa_id
        || user?.empresaId
        || user?.empresa?.id
        || user?.sucursalActiva?.empresa_id
        || user?.sucursalActiva?.empresaId
        || user?.sucursal?.empresa_id
        || user?.sucursal?.empresaId
        || null,
      sucursalId:
        user?.sucursal_activa_id
        || user?.sucursalActiva?.id
        || user?.sucursal?.id
        || user?.sucursal_id
        || null,
    };
  } catch {
    return { empresaId: null, sucursalId: null };
  }
}

function nextYearDate() {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().slice(0, 10);
}

export default function QuickCreateChoferModal({
  isOpen,
  onClose,
  empresaId,
  sucursalId,
  onCreated,
}) {
  const titleId = useId();
  const initialFocusRef = useRef(null);
  const { create } = useChoferMutations();
  const [formState, setFormState] = useState(() => ({
    ...INITIAL_STATE,
    licenciaVencimiento: nextYearDate(),
  }));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const storedContext = useMemo(() => resolveStoredContext(), []);
  const effectiveEmpresaId = empresaId || storedContext.empresaId || null;
  const effectiveSucursalId = sucursalId || storedContext.sucursalId || null;

  const disabledByContext = useMemo(
    () => !effectiveEmpresaId || !effectiveSucursalId,
    [effectiveEmpresaId, effectiveSucursalId],
  );

  const resetState = () => {
    setFormState({
      ...INITIAL_STATE,
      licenciaVencimiento: nextYearDate(),
    });
    setErrors({});
    setMessage('');
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
    setMessage('');
  };

  const validate = () => {
    const nextErrors = {};
    if (!formState.apellido.trim()) nextErrors.apellido = 'Ingresá el apellido.';
    if (!formState.nombre.trim()) nextErrors.nombre = 'Ingresá el nombre.';
    if (!/^\d{7,9}$/.test(formState.nroDocumento.trim())) {
      nextErrors.nroDocumento = 'El DNI debe tener entre 7 y 9 dígitos.';
    }
    if (!formState.telefonoMovil.trim()) nextErrors.telefonoMovil = 'Ingresá un celular.';
    if (!formState.licenciaClase.trim()) nextErrors.licenciaClase = 'Elegí una clase.';
    if (!formState.licenciaVencimiento) nextErrors.licenciaVencimiento = 'Indicá vencimiento.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabledByContext) {
      setMessage('No pudimos resolver empresa y sucursal activas para crear el chofer.');
      return;
    }
    if (!validate()) return;
    setIsSubmitting(true);
    setMessage('');
    try {
      const chofer = await create({
        apellido: formState.apellido.trim(),
        nombre: formState.nombre.trim(),
        tipoDocumento: 'DNI',
        nroDocumento: formState.nroDocumento.trim(),
        telefonoMovil: formState.telefonoMovil.trim(),
        empresaId: String(effectiveEmpresaId),
        sucursalId: String(effectiveSucursalId),
        fechaAlta: new Date().toISOString().slice(0, 10),
        estado: 'ACTIVO',
        pais: 'AR',
        licenciaClase: [formState.licenciaClase],
        licenciaVencimiento: formState.licenciaVencimiento,
        aptoPsicofisico: false,
        docOtros: [],
      });
      onCreated?.(chofer);
      handleClose();
    } catch (error) {
      // urql prefixes server messages with "[GraphQL]"; the operator only needs the reason.
      const reason = error?.graphQLErrors?.[0]?.message || String(error?.message || '').replace(/^\[(GraphQL|Network)\]\s*/, '');
      setMessage(reason || 'No se pudo crear el chofer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy={titleId}
      initialFocusRef={initialFocusRef}
      className="modal modal-content w-full max-w-[680px] p-5 md:p-6 focus-visible:outline-none"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="text-xl font-semibold text-[rgb(var(--fg))]">
              Alta rápida de chofer
            </h2>
            <p className="mt-1 text-sm text-[rgba(var(--fg),0.68)]">
              Cargá los datos mínimos y seguí sin salir de esta pantalla.
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={handleClose} disabled={isSubmitting}>
            Cerrar
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
              Apellido
            </label>
            <Input
              ref={initialFocusRef}
              name="apellido"
              value={formState.apellido}
              onChange={handleChange}
              placeholder="Apellido"
            />
            {errors.apellido ? <p className="mt-1 text-xs text-red-300">{errors.apellido}</p> : null}
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
              Nombre
            </label>
            <Input name="nombre" value={formState.nombre} onChange={handleChange} placeholder="Nombre" />
            {errors.nombre ? <p className="mt-1 text-xs text-red-300">{errors.nombre}</p> : null}
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
              DNI
            </label>
            <Input
              name="nroDocumento"
              value={formState.nroDocumento}
              onChange={handleChange}
              inputMode="numeric"
              placeholder="Sin puntos"
            />
            {errors.nroDocumento ? <p className="mt-1 text-xs text-red-300">{errors.nroDocumento}</p> : null}
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
              Celular
            </label>
            <Input
              name="telefonoMovil"
              value={formState.telefonoMovil}
              onChange={handleChange}
              placeholder="11 5555 5555"
            />
            {errors.telefonoMovil ? <p className="mt-1 text-xs text-red-300">{errors.telefonoMovil}</p> : null}
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
              Clase de licencia
            </label>
            <Select name="licenciaClase" value={formState.licenciaClase} onChange={handleChange}>
              <option value="A">Clase A</option>
              <option value="B">Clase B</option>
              <option value="C">Clase C</option>
              <option value="D">Clase D</option>
              <option value="E">Clase E</option>
              <option value="F">Clase F</option>
              <option value="G">Clase G</option>
            </Select>
            {errors.licenciaClase ? <p className="mt-1 text-xs text-red-300">{errors.licenciaClase}</p> : null}
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
              Vencimiento licencia
            </label>
            <Input
              type="date"
              name="licenciaVencimiento"
              value={formState.licenciaVencimiento}
              onChange={handleChange}
            />
            {errors.licenciaVencimiento ? <p className="mt-1 text-xs text-red-300">{errors.licenciaVencimiento}</p> : null}
          </div>
        </div>

        {message ? (
          <div className="rounded-2xl border border-red-400/35 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {message}
          </div>
        ) : null}

        {disabledByContext ? (
          <div className="rounded-2xl border border-amber-400/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-50">
            Falta resolver empresa o sucursal activa para crear el chofer en esta sesión.
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting || disabledByContext}>
            {isSubmitting ? 'Guardando...' : 'Guardar chofer'}
          </Button>
        </div>
      </form>
    </ModalBase>
  );
}
